import { useEffect, useMemo, useState } from "react";
import html2canvas from "html2canvas";

import ImagePreview from "./ImagePreview";
import fourHEmblem from "../assets/fourHEmblem.png";

import "../styles/CreatePDFModal.css";

const parseAnswers = (rawAnswers) => {
  if (Array.isArray(rawAnswers)) {
    return rawAnswers;
  }

  if (typeof rawAnswers !== "string") {
    return [];
  }

  try {
    const parsedAnswers = JSON.parse(rawAnswers);

    return Array.isArray(parsedAnswers)
      ? parsedAnswers
      : [];
  } catch (error) {
    console.error(
      "Unable to parse assessment answers:",
      error
    );

    return [];
  }
};

const normalizeText = (value) =>
  String(value || "").trim().toLowerCase();

const buildFullName = (user) =>
  [
    user?.name_first,
    user?.name_middle,
    user?.name_last,
  ]
    .filter(Boolean)
    .join(" ");

const truncateText = (text, maxCharacters = 45) => {
  const normalizedText = String(text || "");

  if (normalizedText.length <= maxCharacters) {
    return normalizedText;
  }

  return `${normalizedText
    .slice(0, maxCharacters)
    .trim()}...`;
};

const calculatePercentage = (
  rawScore,
  maximumScore
) => {
  const score = Number(rawScore) || 0;

  if (!maximumScore) {
    return 0;
  }

  return Math.round(
    ((score * 3) / maximumScore) * 100
  );
};

export default function CreatePDFModal({
  userId,
  testId,
  users = [],
  assessments = [],
  questions = [],
  handlePDFSubmit,
  onCancel,
}) {
  const [answers, setAnswers] = useState({});
  const [isGenerating, setIsGenerating] =
    useState(false);

  const [error, setError] = useState("");

  const user = users.find(
    (candidate) =>
      candidate.four_h_id === userId
  );

  const assessment = assessments.find(
    (candidate) =>
      candidate.user_id === userId &&
      candidate.test_id === testId
  );

  useEffect(() => {
    if (!assessment) {
      setAnswers({});
      return;
    }

    const answerMap = parseAnswers(
      assessment.answers
    ).reduce((currentAnswers, answer) => {
      let userAnswer = "";
      let corrected = Boolean(answer.corrected);

      if (Array.isArray(answer.uAnswer)) {
        userAnswer = normalizeText(
          answer.uAnswer[0]
        );

        corrected =
          corrected ||
          Boolean(
            answer.uAnswer[1]?.corrected
          );
      } else {
        userAnswer = normalizeText(
          answer.uAnswer
        );
      }

      currentAnswers[answer.questionId] = {
        userAnswer,
        corrected,
      };

      return currentAnswers;
    }, {});

    setAnswers(answerMap);
  }, [assessment]);

  const filteredQuestions = useMemo(() => {
    if (!user) {
      return [];
    }

    return questions.filter(
      (question) =>
        String(question.level) ===
        String(user.level)
    );
  }, [questions, user]);

  const knowledgeQuestions =
    filteredQuestions.filter(
      (question) =>
        question.question_type === "Knowledge"
    );

  const identificationQuestions =
    filteredQuestions.filter(
      (question) =>
        question.question_type ===
        "Identification"
    );

  const getChoiceClassName = (
    question,
    choice
  ) => {
    const answer = answers[question.id];

    if (
      !answer ||
      answer.userAnswer !== normalizeText(choice)
    ) {
      return "choiceP";
    }

    const isCorrect =
      normalizeText(choice) ===
      normalizeText(question.answer);

    return `choiceP ${
      isCorrect
        ? "correctAnswerHighlight"
        : "wrongAnswerHighlight"
    }`;
  };

  const handleGeneratePDF = async () => {
    if (!user || !assessment) {
      setError(
        "The participant or assessment could not be found."
      );
      return;
    }

    setIsGenerating(true);
    setError("");

    try {
      const firstPage =
        document.getElementById("firstPage");

      const secondPage =
        document.getElementById("secondPage");

      if (!firstPage || !secondPage) {
        throw new Error(
          "The PDF page content could not be found."
        );
      }

      const [firstCanvas, secondCanvas] =
        await Promise.all([
          html2canvas(firstPage, {
            scale: 2,
            backgroundColor: "#ffffff",
            useCORS: true,
          }),
          html2canvas(secondPage, {
            scale: 2,
            backgroundColor: "#ffffff",
            useCORS: true,
          }),
        ]);

      const pageOne = firstCanvas
        .toDataURL("image/jpeg", 1)
        .split(",")[1];

      const pageTwo = secondCanvas
        .toDataURL("image/jpeg", 1)
        .split(",")[1];

      await handlePDFSubmit(
        user.four_h_id,
        assessment.test_id,
        {
          page_one: pageOne,
          page_two: pageTwo,
        },
        user.team
      );
    } catch (generationError) {
      console.error(
        "Unable to generate assessment PDF:",
        generationError
      );

      setError(
        generationError?.message ||
          "The PDF could not be generated."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  if (!user || !assessment) {
    return (
      <div className="pdfModalContainer">
        <p className="fourHFormStatus error">
          The participant or assessment could not be found.
        </p>

        {typeof onCancel === "function" && (
          <button
            type="button"
            className="submitUserUpdateButton"
            onClick={onCancel}
          >
            Close
          </button>
        )}
      </div>
    );
  }

  const knowledgeScore =
    Number(assessment.knowledge_score) || 0;

  const identificationScore =
    Number(assessment.identify_score) || 0;

  return (
    <div
      className="pdfModalContainer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-pdf-title"
    >
      <div className="pdfModalTopbar">
        <p id="create-pdf-title">
          <strong>
            Creating PDF for User ID: {userId}
          </strong>
        </p>

        {typeof onCancel === "function" && (
          <button
            type="button"
            className="closePDFModalBtn"
            onClick={onCancel}
            disabled={isGenerating}
            aria-label="Close PDF creator"
          >
            ×
          </button>
        )}
      </div>

      {error && (
        <p className="fourHFormStatus error" role="alert">
          {error}
        </p>
      )}

      <div
        id="pdfDiv"
        className="pdfTestContainer"
      >
        <section id="firstPage" className="page">
          <header className="pdfHeader">
            <div className="headerImg">
              <img
                className="pdfLogoImage"
                src={fourHEmblem}
                alt="4-H emblem"
              />
            </div>

            <p>
              Participant ID:{" "}
              <strong>{user.four_h_id}</strong>{" "}
              Name:{" "}
              <strong>
                {buildFullName(user)}
              </strong>
            </p>

            <p>
              Team: <strong>{user.team}</strong>
            </p>

            <p>
              Multiple Choice Score:{" "}
              <strong>{knowledgeScore * 3}</strong>
              /30 Percentage:{" "}
              <strong>
                {calculatePercentage(
                  knowledgeScore,
                  30
                )}
                %
              </strong>
            </p>

            <h2>Tester Responses</h2>
          </header>

          <h3 className="sectionHeaders">
            Multiple Choice Questions
          </h3>

          {knowledgeQuestions.map((question) => (
            <div
              key={question.id}
              className={
                question.id === 8
                  ? "page-break-before"
                  : undefined
              }
            >
              <p>
                Question {question.id}:{" "}
                <strong>
                  {question.question}
                </strong>
              </p>

              <div className="optionsP">
                <p
                  className={getChoiceClassName(
                    question,
                    question.choice_a
                  )}
                >
                  {truncateText(
                    question.choice_a
                  )}
                </p>

                <p
                  className={getChoiceClassName(
                    question,
                    question.choice_b
                  )}
                >
                  {truncateText(
                    question.choice_b
                  )}
                </p>

                {question.choice_c && (
                  <p
                    className={getChoiceClassName(
                      question,
                      question.choice_c
                    )}
                  >
                    {truncateText(
                      question.choice_c
                    )}
                  </p>
                )}

                {question.choice_d && (
                  <p
                    className={getChoiceClassName(
                      question,
                      question.choice_d
                    )}
                  >
                    {truncateText(
                      question.choice_d
                    )}
                  </p>
                )}
              </div>
            </div>
          ))}
        </section>

        <section id="secondPage" className="page">
          <header className="pdfHeader">
            <div className="headerImg">
              <img
                className="pdfLogoImage"
                src={fourHEmblem}
                alt="4-H emblem"
              />
            </div>

            <p>
              Participant ID:{" "}
              <strong>{user.four_h_id}</strong>{" "}
              Name:{" "}
              <strong>
                {buildFullName(user)}
              </strong>
            </p>

            <p>
              Team: <strong>{user.team}</strong>
            </p>

            <p>
              Identification Score:{" "}
              <strong>
                {identificationScore * 3}
              </strong>
              /30 Percentage:{" "}
              <strong>
                {calculatePercentage(
                  identificationScore,
                  30
                )}
                %
              </strong>
            </p>

            <h2>Tester Responses</h2>
          </header>

          <h3 className="sectionHeaders">
            Identification Questions
          </h3>

          {identificationQuestions.map(
            (question) => {
              const answer =
                answers[question.id];

              const isCorrect =
                answer &&
                (answer.userAnswer ===
                  normalizeText(
                    question.answer
                  ) ||
                  answer.corrected);

              return (
                <div
                  key={question.id}
                  className="id-question-container"
                >
                  <p className="idQuestionP">
                    Question{" "}
                    <strong>
                      {question.id}:
                    </strong>
                  </p>

                  <ImagePreview
                    base64String={question.image}
                    className="pdfQuestionImage"
                  />

                  <p
                    className={`userAnswerSpan ${
                      isCorrect
                        ? "crrctAnswerIdHighlight"
                        : "wrngAnswerIdHighlight"
                    }`}
                  >
                    {answer?.userAnswer || ""}
                  </p>
                </div>
              );
            }
          )}
        </section>
      </div>

      <div className="pdfModalActions">
        <button
          type="button"
          className="createPDFBtn"
          onClick={handleGeneratePDF}
          disabled={isGenerating}
        >
          {isGenerating
            ? "Creating PDF..."
            : "Submit PDF"}
        </button>

        {typeof onCancel === "function" && (
          <button
            type="button"
            className="submitUserUpdateButton"
            onClick={onCancel}
            disabled={isGenerating}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}